import { useLocation, useHistory } from "react-router-dom";
import { useFocusToInput } from "utility/useFocusToInput";

import { useI18n } from "../../../../hooks/i18n/i18n";
import { KEYWORD_MAX_COUNT } from "../../constants/keywordMaxCount";
import { convertQueryToSearchCondition } from "../../functions/convertQueryToSearchCondition";
import { getNextQuery } from "./functions/getNextQuery";
import { useFormState } from "./functions/useFormState";
import {
  createAreaCheckboxAction,
  createAvailabilityStatusAction,
  createCollapseAction,
  createEndDateAction,
  createEventTypeAction,
  createGenreAction,
  createPrefecturesAction,
  createStartDateAction,
} from "./functions/useFormState/functioins/createActions";
import { useRealtimeResultCount } from "./functions/useRealtimeResultCount";

import type { CommonConditionsState } from "./functions/useFormState/types/states";
import type { Props } from "./props";
import type { FormEventHandler } from "react";

export const useThis = (input: Props) => {
  const { genreConditions, keyword, onClose } = input;
  const inputRef = useFocusToInput();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { t } = useI18n();
  const { dispatchFormAction, ...state } = useFormState({
    ...convertQueryToSearchCondition({ search }),
    genreConditions,
  });
  const { canSearch, resultCount } = useRealtimeResultCount({
    keyword,
    ...state,
  });

  const handleSelectStartDate = (date: Date | null) => {
    dispatchFormAction(createStartDateAction(date ?? undefined));
  };
  const handleSelectEndDate = (date: Date | null) => {
    dispatchFormAction(createEndDateAction(date ?? undefined));
  };

  // モーダル外クリックで閉じる
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleSearch: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const nextQuery = getNextQuery({
      ...state,
      keyword,
      search,
    });
    const location = [pathname, nextQuery].filter(Boolean).join("?");
    history.push(location);
    onClose();
  };

  const keywordInputTitle = t("search.upperLimitOfKeyword", {
    maxCount: KEYWORD_MAX_COUNT.toString(),
  });
  const {
    availabilityStatus,
    endDate,
    eventType,
    genre,
    startDate,
    ...restState
  } = state;
  const handleCheckArea = (areaCode: string) => {
    dispatchFormAction(createAreaCheckboxAction(areaCode));
  };
  const handleCheckPrefecture = (prefectureCode: string) => {
    dispatchFormAction(createPrefecturesAction(prefectureCode));
  };
  const handleCollapseArea = (areaCode: string) => {
    dispatchFormAction(createCollapseAction(areaCode));
  };

  type CommonConditions = Array<{
    commonCondition: CommonConditionsState;
    handleCheck: (checkboxId: string) => void;
  }>;
  const commonConditions: CommonConditions = [
    {
      commonCondition: genre,
      handleCheck: (checkboxId) => {
        dispatchFormAction(createGenreAction(checkboxId));
      },
    },
    {
      commonCondition: eventType,
      handleCheck: (checkboxId) => {
        dispatchFormAction(createEventTypeAction(checkboxId));
      },
    },
    {
      commonCondition: availabilityStatus,
      handleCheck: (checkboxId) => {
        dispatchFormAction(createAvailabilityStatusAction(checkboxId));
      },
    },
  ];

  return {
    ...restState,
    canSearch,
    commonConditions,
    endDate,
    endMinDate: startDate ? new Date(startDate) : undefined,
    handleCheckArea,
    handleCheckPrefecture,
    handleCollapseArea,
    handleOverlayClick,
    handleSearch,
    handleSelectEndDate,
    handleSelectStartDate,
    inputRef,
    keywordInputTitle,
    resultCount,
    startDate,
    startMaxDate: endDate ? new Date(endDate) : undefined,
    t,
  };
};
